<template>
  <div :class="(label ? 'input-group ' : '') + 'max-pos-picker'" data-max-host>
    <div v-if="label" :id="addonId" class="input-group-addon">{{ label }}</div>
    <client-only>
      <v-select
        ref="select"
        class="max-pos-picker__select"
        :options="merged"
        label="label"
        :multiple="multiple"
        :clearable="true"
        :filterable="!serverSearch"
        :autoscroll="false"
        :disabled="disabled"
        :placeholder="placeholder"
        :aria-labelledby="label ? addonId : undefined"
        :modelValue="selected"
        v-on:update:modelValue="onUpdate"
        v-on:search="onSearch"
        v-on:open="onOpen"
        v-on:close="onClose"
      >
        <template v-slot:no-options="{ searching }">
          <span>{{ searching ? _('No results found.') : _('Type to search') }}</span>
        </template>
      </v-select>
    </client-only>
  </div>
</template>

<script>
import { useId } from 'vue'
import { useMaxInterface } from '#max-runtime/context.ts'

const LIVE_DELAY_MS = 300
const SCROLL_GAP = 48

export default {
  props: {
    options: { type: Array, default: () => [] },
    modelValue: { default: null },
    multiple: { type: Boolean, default: false },
    label: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    optionsState: { type: String, default: null }
  },
  emits: ['update:modelValue'],
  setup () {
    const { $translate } = useNuxtApp()
    return { iface: useMaxInterface(), addonId: 'max-pos-picker-' + useId(), _: $translate }
  },
  data () {
    return {
      query: '',
      more: null,
      remembered: {}
    }
  },
  created () {
    this.timer = null
    this.menu = null
    this.inFlight = false
    this.searchGen = 0
    this.rememberAll(this.options)
  },
  beforeUnmount () {
    if (this.timer) clearTimeout(this.timer)
    this.unbindMenu()
  },
  computed: {
    busy () {
      return !!(this.iface && this.iface.busy.value)
    },
    serverSearch () {
      return !!(this.optionsState && this.iface && this.iface.canSearchList(this.optionsState))
    },
    flat () {
      const out = []
      for (const option of this.options || []) {
        if (option && option.options) out.push(...option.options)
        else if (option) out.push(option)
      }
      return out
    },
    values () {
      if (this.multiple) return Array.isArray(this.modelValue) ? this.modelValue : []
      return this.modelValue === null || this.modelValue === undefined || this.modelValue === '' ? [] : [this.modelValue]
    },
    selected () {
      const items = this.values.map(value => this.optionFor(value))
      return this.multiple ? items : (items[0] ?? null)
    },
    merged () {
      const seen = new Set(this.flat.map(option => String(option.value)))
      const extra = this.values
        .filter(value => !seen.has(String(value)))
        .map(value => this.optionFor(value))
      return extra.concat(this.flat)
    },
    showMore () {
      if (!this.serverSearch) return false
      if (this.more === true) return true
      if (this.more === false) return false
      return this.flat.length >= 50
    }
  },
  watch: {
    options (list) {
      this.rememberAll(list)
      const top = this.menu ? this.menu.scrollTop : null
      this.$nextTick(() => {
        if (this.menu && top != null) this.menu.scrollTop = top
      })
    }
  },
  methods: {
    rememberAll (list) {
      for (const option of list || []) {
        if (option && option.options) this.rememberAll(option.options)
        else if (option && option.value !== undefined) this.remembered[String(option.value)] = option.label
      }
    },
    optionFor (value) {
      const found = this.flat.find(option => String(option.value) === String(value))
      if (found) return found
      return { label: this.remembered[String(value)] || String(value), value }
    },
    onUpdate (selected) {
      if (this.multiple) {
        const items = Array.isArray(selected) ? selected : []
        items.forEach(item => { if (item) this.remembered[String(item.value)] = item.label })
        this.$emit('update:modelValue', items.map(item => item && item.value).filter(v => v !== undefined && v !== null))
        return
      }
      if (selected) this.remembered[String(selected.value)] = selected.label
      this.$emit('update:modelValue', selected ? selected.value : null)
    },
    onSearch (query) {
      this.query = query
      if (!this.serverSearch) return
      clearTimeout(this.timer)
      this.timer = setTimeout(() => this.search(query, 0), LIVE_DELAY_MS)
    },
    async search (query, offset) {
      if (!this.iface || !this.optionsState) return
      if (this.inFlight && offset > 0) return
      const gen = ++this.searchGen
      this.inFlight = true
      try {
        const result = await this.iface.searchList(this.optionsState, query, offset)
        if (gen !== this.searchGen) return
        this.more = result.more
        if (offset === 0) this.$nextTick(() => this.fillIfShort())
      } finally {
        if (gen === this.searchGen) this.inFlight = false
      }
    },
    loadMore () {
      if (!this.showMore || this.inFlight) return
      return this.search(this.query, this.flat.length)
    },
    onOpen () {
      this.$nextTick(() => {
        this.bindMenu()
        this.fillIfShort()
      })
    },
    onClose () {
      this.unbindMenu()
    },
    bindMenu () {
      this.unbindMenu()
      const root = this.$el
      const menu = root && root.querySelector ? root.querySelector('.vs__dropdown-menu') : null
      if (!menu) return
      this.menu = menu
      menu.addEventListener('scroll', this.onMenuScroll, { passive: true })
    },
    unbindMenu () {
      if (this.menu) this.menu.removeEventListener('scroll', this.onMenuScroll)
      this.menu = null
    },
    onMenuScroll () {
      const menu = this.menu
      if (!menu || this.inFlight || !this.showMore) return
      if (menu.scrollTop + menu.clientHeight >= menu.scrollHeight - SCROLL_GAP) this.loadMore()
    },
    fillIfShort () {
      const menu = this.menu
      if (!menu || this.inFlight || !this.showMore) return
      if (menu.scrollHeight <= menu.clientHeight + SCROLL_GAP) this.loadMore()
    }
  }
}
</script>

<style scoped>
.max-pos-picker {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: stretch;
}
.max-pos-picker .input-group-addon {
  display: flex;
  align-items: center;
  width: auto;
  flex: 0 0 auto;
  height: auto;
  box-sizing: border-box;
}
.max-pos-picker__select {
  flex: 1 1 auto;
  min-width: 0;
}
.max-pos-picker :deep(.v-select) {
  min-height: 44px;
}
.max-pos-picker :deep(.vs__dropdown-toggle) {
  box-sizing: border-box;
  height: 44px;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid #cfd8dc;
  border-radius: 0;
  background-color: #eceff1;
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075);
  align-items: center;
  overflow: hidden;
}
.max-pos-picker :deep(.vs--multiple .vs__dropdown-toggle) {
  height: auto;
}
.max-pos-picker :deep(.vs__selected-options) {
  min-width: 0;
  padding: 0;
  align-items: center;
  overflow: hidden;
}
.max-pos-picker :deep(.vs__search),
.max-pos-picker :deep(.vs__search:focus) {
  flex: 1 1 auto;
  min-width: 0;
  width: 0;
  margin: 0;
  padding: 0;
  height: 22px;
  line-height: 22px;
  font-size: 16px;
  color: #455964;
  border: 0;
  box-shadow: none;
}
.max-pos-picker :deep(.vs__dropdown-menu) {
  overflow-x: hidden;
  overflow-y: auto;
}
.max-pos-picker :deep(.vs__dropdown-option) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.max-pos-picker :deep(.vs__selected) {
  margin: 0 4px 0 0;
  padding: 0;
  max-width: 100%;
  line-height: 22px;
  font-size: 16px;
  color: #455964;
  border: 0;
  background: transparent;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.max-pos-picker :deep(.vs__actions) {
  padding: 0;
}
.max-pos-picker :deep(.vs__clear),
.max-pos-picker :deep(.vs__open-indicator) {
  fill: #455964;
}
</style>
